/**
 * ADR-61 cut 2: the platform reads the constitution and makes a case.
 *
 * Each judge here is pure: it takes what the platform OBSERVED (a pull
 * request and its checks, a benchmark file, a telemetry reading per target
 * ring, the freeze, the guard, the exposure, the classification) and returns
 * a verdict with the evidence that produced it: what was observed, what was
 * required, and why the verdict follows. The control plane writes that as an
 * immutable gate row (ops_change_gates) and never a boolean in text.
 *
 * Nothing here executes anything. Cut 2 gives the platform the right to
 * evaluate a proposed change and say whether it is safe to approve, and no
 * right to touch the controls (that is cut 3, behind the same constitution).
 *
 * The ADR-49 rule holds throughout: absence of evidence is never a pass.
 * A reader that could not read produces FAIL with the reason, or
 * NOT_APPLICABLE when the change genuinely has no such surface; never PASS.
 */

import type { ChangeType } from './change.js';
import type { FreezeVerdict } from './change.js';
import type { GuardVerdict, LiveExposure } from './guard.js';
import { type ChangeSurface, type Classification, type Gate, type GateVerdict, type PolicyDefinition, type PolicyLimits } from './governance.js';

/** The gates cut 2 evaluates from evidence the platform already holds. The other two need the executor. */
export const PLATFORM_EVALUABLE_GATES = [
  'TESTS_GREEN', 'BENCHMARK_CLEAN', 'TELEMETRY_PRESENT', 'NO_ACTIVE_SEV1', 'AUCTION_GUARD', 'BLAST_RADIUS_WITHIN_POLICY', 'HUMAN_LINE_CLEAR',
] as const satisfies readonly Gate[];
export type PlatformEvaluableGate = (typeof PLATFORM_EVALUABLE_GATES)[number];

/** Gates a person records from a rehearsal record until the executor produces them. */
export const HUMAN_RECORDED_GATES = ['REHEARSAL_CLEAN', 'ROLLBACK_PROVEN'] as const satisfies readonly Gate[];

/** Every gate evaluation carries these three, whatever else it carries. */
export interface GateEvidence {
  readonly observed: string;
  readonly required: string;
  readonly because: string;
  readonly [k: string]: unknown;
}
export interface GateJudgement {
  readonly gate: Gate;
  readonly verdict: GateVerdict;
  readonly evidence: GateEvidence;
}

// ---------------------------------------------------------------------------
// TESTS_GREEN: the change's code is on main with green checks.
// ---------------------------------------------------------------------------

export interface PullRequestObservation {
  readonly repo: string;
  readonly number: number;
  readonly url: string;
  readonly state: 'open' | 'closed';
  readonly merged: boolean;
  readonly baseRef: string;
  readonly headSha: string;
  readonly mergeCommitSha: string | null;
}
export interface CheckRunObservation {
  readonly name: string;
  readonly status: string;
  readonly conclusion: string | null;
}
export type TestsGreenInput =
  | { readonly kind: 'no-link' }
  | { readonly kind: 'unreadable'; readonly ref: string; readonly why: string }
  | { readonly kind: 'observed'; readonly pr: PullRequestObservation; readonly checks: readonly CheckRunObservation[]; readonly checkedSha: string };

const GREEN = new Set(['success', 'skipped', 'neutral']);

export function judgeTestsGreen(input: TestsGreenInput): GateJudgement {
  const required = 'the linked pull request is merged into main and every check on the merged commit concluded green';
  if (input.kind === 'no-link') {
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { observed: 'no GITHUB_PR link on the change', required, because: 'nothing to read: link the pull request that carries the change (PULL_REQUEST or RELATED_TO, kind GITHUB_PR)' } };
  }
  if (input.kind === 'unreadable') {
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { observed: `GitHub could not be read for ${input.ref}: ${input.why}`, required, because: 'absence of evidence is never a pass (ADR-49)', ref: input.ref } };
  }
  const { pr, checks, checkedSha } = input;
  const detail = { repo: pr.repo, number: pr.number, url: pr.url, state: pr.state, merged: pr.merged, baseRef: pr.baseRef, headSha: pr.headSha, mergeCommitSha: pr.mergeCommitSha, checkedSha, checks: checks.map((c) => ({ name: c.name, status: c.status, conclusion: c.conclusion })) };
  if (!pr.merged) {
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { ...detail, observed: `PR #${pr.number} is ${pr.state} and not merged`, required, because: 'the code is not on main yet' } };
  }
  if (pr.baseRef !== 'main') {
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { ...detail, observed: `PR #${pr.number} merged into ${pr.baseRef}`, required, because: 'merged, but not into main' } };
  }
  if (checks.length === 0) {
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { ...detail, observed: `PR #${pr.number} merged (${checkedSha.slice(0, 7)}) with no check runs on the commit`, required, because: 'no check ran on the merged commit, so nothing says the tests were green' } };
  }
  const notDone = checks.filter((c) => c.status !== 'completed');
  const red = checks.filter((c) => c.status === 'completed' && !GREEN.has(c.conclusion ?? ''));
  if (notDone.length > 0 || red.length > 0) {
    const what = [...red.map((c) => `${c.name}: ${c.conclusion ?? 'no conclusion'}`), ...notDone.map((c) => `${c.name}: ${c.status}`)].join('; ');
    return { gate: 'TESTS_GREEN', verdict: 'FAIL', evidence: { ...detail, observed: `PR #${pr.number} merged (${checkedSha.slice(0, 7)}); ${what}`, required, because: red.length > 0 ? 'a check on the merged commit did not conclude green' : 'a check on the merged commit has not completed' } };
  }
  return { gate: 'TESTS_GREEN', verdict: 'PASS', evidence: { ...detail, observed: `PR #${pr.number} merged into main (${checkedSha.slice(0, 7)}); ${checks.length} check(s) green: ${checks.map((c) => c.name).join(', ')}`, required, because: 'merged into main and every check on the merged commit concluded green' } };
}

// ---------------------------------------------------------------------------
// BENCHMARK_CLEAN: the harness's own verdict, at the fleet sizes the policy names.
// ---------------------------------------------------------------------------

export interface BenchmarkResultLine { readonly mode: string; readonly bidders: number; readonly pass: boolean }
export interface BenchmarkObservation { readonly at: string | null; readonly results: readonly BenchmarkResultLine[] }

/** The harness file, as `packages/loadtest` writes it: { at, args, results: [{ mode, bidders, pass, clean, lossy }] }. Anything else is not a benchmark. */
export function parseBenchmarkFile(json: unknown): { ok: true; value: BenchmarkObservation } | { ok: false; why: string } {
  if (!json || typeof json !== 'object') return { ok: false, why: 'not an object' };
  const o = json as { at?: unknown; results?: unknown };
  if (!Array.isArray(o.results) || o.results.length === 0) return { ok: false, why: 'no results array' };
  const results: BenchmarkResultLine[] = [];
  for (const r of o.results as unknown[]) {
    const x = r as { mode?: unknown; bidders?: unknown; pass?: unknown };
    if (typeof x.mode !== 'string' || typeof x.bidders !== 'number' || typeof x.pass !== 'boolean') return { ok: false, why: 'a result lacks mode, bidders or pass' };
    results.push({ mode: x.mode, bidders: x.bidders, pass: x.pass });
  }
  return { ok: true, value: { at: typeof o.at === 'string' ? o.at : null, results } };
}

export const DEFAULT_BENCHMARK_SIZES = [100, 500, 1_000] as const;

export function judgeBenchmark(input: { readonly file: unknown } | { readonly file: null }, sizes: readonly number[] = DEFAULT_BENCHMARK_SIZES): GateJudgement {
  const required = `the harness's own verdict is PASS for every mode at every required fleet size (${sizes.join(', ')} bidders)`;
  if (input.file === null || input.file === undefined) {
    return { gate: 'BENCHMARK_CLEAN', verdict: 'FAIL', evidence: { observed: 'no benchmark file attached to this evaluation', required, because: 'attach the harness JSON (packages/loadtest results) to the evaluation; absence of evidence is never a pass', sizes } };
  }
  const parsed = parseBenchmarkFile(input.file);
  if (!parsed.ok) return { gate: 'BENCHMARK_CLEAN', verdict: 'FAIL', evidence: { observed: `the attachment is not a harness result file: ${parsed.why}`, required, because: 'only the harness file counts; a hand-written summary is not evidence', sizes } };
  const lines = parsed.value.results;
  const failed = lines.filter((l) => !l.pass);
  const missing = sizes.filter((s) => !lines.some((l) => l.bidders === s));
  const detail = { at: parsed.value.at, sizes, results: lines };
  if (failed.length > 0) {
    return { gate: 'BENCHMARK_CLEAN', verdict: 'FAIL', evidence: { ...detail, observed: `harness FAIL: ${failed.map((l) => `${l.mode} at ${l.bidders}`).join('; ')}`, required, because: 'the harness itself failed a run' } };
  }
  if (missing.length > 0) {
    return { gate: 'BENCHMARK_CLEAN', verdict: 'FAIL', evidence: { ...detail, observed: `results at ${[...new Set(lines.map((l) => l.bidders))].join(', ')} bidders; none at ${missing.join(', ')}`, required, because: 'a required fleet size was not run' } };
  }
  return { gate: 'BENCHMARK_CLEAN', verdict: 'PASS', evidence: { ...detail, observed: `${lines.length} run(s) PASS: ${lines.map((l) => `${l.mode} at ${l.bidders}`).join('; ')}`, required, because: 'every run the harness reports passed, and every required size was run' } };
}

// ---------------------------------------------------------------------------
// TELEMETRY_PRESENT: every target ring is reporting, fresh, right now.
// ---------------------------------------------------------------------------

export interface TelemetryTargetObservation {
  readonly saleId: string;
  readonly ringId: string;
  readonly evidence: 'observed' | 'stale' | 'absent';
  readonly why: string | null;
  /** Metric names present in the report when observed. */
  readonly metrics: readonly string[];
  readonly biddersConnected: number | null;
}

/** Surface areas whose validation reads ring telemetry. */
const RUNTIME_AREAS = new Set<ChangeSurface['areas'][number]>(['RING_RUNTIME_FLAG', 'BIDDING_SEMANTICS', 'DURABILITY', 'AUCTION_FORMAT', 'DEPLOYMENT']);

export function judgeTelemetry(surface: ChangeSurface, targets: readonly TelemetryTargetObservation[]): GateJudgement {
  const required = 'every auction and ring the change names is reporting runtime telemetry now, fresh within the budget';
  const runtime = surface.areas.some((a) => RUNTIME_AREAS.has(a));
  if (targets.length === 0) {
    if (!runtime) return { gate: 'TELEMETRY_PRESENT', verdict: 'NOT_APPLICABLE', evidence: { observed: 'the change names no auction or ring and touches no runtime area', required, because: 'nothing on this surface is validated by ring telemetry' } };
    return { gate: 'TELEMETRY_PRESENT', verdict: 'FAIL', evidence: { observed: 'the change touches the runtime but names no auction and ring (impacts AUCTION + RING)', required, because: 'without a target there is nothing to observe; name the auction and ring on the change' } };
  }
  const bad = targets.filter((t) => t.evidence !== 'observed');
  const detail = { targets: targets.map((t) => ({ saleId: t.saleId, ringId: t.ringId, evidence: t.evidence, why: t.why, metrics: t.metrics, biddersConnected: t.biddersConnected })) };
  if (bad.length > 0) {
    return { gate: 'TELEMETRY_PRESENT', verdict: 'FAIL', evidence: { ...detail, observed: bad.map((t) => `${t.ringId} of ${t.saleId}: ${t.evidence}${t.why ? ` (${t.why})` : ''}`).join('; '), required, because: 'a target ring is not observed right now; absence of evidence is never a pass' } };
  }
  return { gate: 'TELEMETRY_PRESENT', verdict: 'PASS', evidence: { ...detail, observed: targets.map((t) => `${t.ringId} of ${t.saleId}: observed, ${t.metrics.length} metric(s), ${t.biddersConnected ?? 'unknown'} bidder(s) connected`).join('; '), required, because: 'every target ring answered with a fresh report' } };
}

// ---------------------------------------------------------------------------
// NO_ACTIVE_SEV1 and AUCTION_GUARD: ADR-58, as today.
// ---------------------------------------------------------------------------

export function judgeNoActiveSev1(freeze: FreezeVerdict): GateJudgement {
  const required = 'no SEV1 incident is active, or the change is an EMERGENCY change linked to one';
  if (freeze.blocked) return { gate: 'NO_ACTIVE_SEV1', verdict: 'FAIL', evidence: { observed: freeze.reason ?? 'frozen', required, because: 'the ADR-58 freeze is in force', incidents: freeze.incidents } };
  return { gate: 'NO_ACTIVE_SEV1', verdict: 'PASS', evidence: { observed: freeze.incidents.length === 0 ? 'no active SEV1' : `active SEV1 ${freeze.incidents.join(', ')}; this EMERGENCY change is linked to it`, required, because: 'the freeze does not apply', incidents: freeze.incidents } };
}

export function judgeAuctionGuard(type: ChangeType, guard: GuardVerdict, exposure: LiveExposure): GateJudgement {
  const required = 'no bidding is open right now, or the change is an EMERGENCY change';
  const detail = { exposure };
  if (guard.requiresOverride) return { gate: 'AUCTION_GUARD', verdict: 'FAIL', evidence: { ...detail, observed: guard.reason ?? 'bidding open', required, because: `a ${type} change would enter production during live bidding; an override with a reason is a human decision on the transition, not a pass here` } };
  return { gate: 'AUCTION_GUARD', verdict: 'PASS', evidence: { ...detail, observed: exposure.biddingOpenSales === 0 ? `no bidding open (${exposure.liveSales} live sale(s))` : `bidding open on ${exposure.biddingOpenSales} sale(s); EMERGENCY change`, required, because: 'the auction guard does not apply right now' } };
}

// ---------------------------------------------------------------------------
// BLAST_RADIUS_WITHIN_POLICY: the declared reach against the policy in force.
// ---------------------------------------------------------------------------

export function judgeBlastRadius(surface: ChangeSurface, limits: PolicyLimits | null, exposure: LiveExposure): GateJudgement {
  const required = 'the declared reach (tenants, sales, rings) is within the limits of the policy in force';
  const br = surface.blastRadius;
  if (!br) return { gate: 'BLAST_RADIUS_WITHIN_POLICY', verdict: 'FAIL', evidence: { observed: 'no blast radius declared on the change', required, because: 'declare tenants, sales and rings reached; undeclared reach is line 5', exposure } };
  if (!limits) return { gate: 'BLAST_RADIUS_WITHIN_POLICY', verdict: 'FAIL', evidence: { observed: `declared ${br.tenants} tenant(s), ${br.sales} sale(s), ${br.rings} ring(s); no policy in force names a limit`, required, because: 'with no policy there is no limit to be within; a human decides (line 5)', declared: br, exposure } };
  const over = (['tenants', 'sales', 'rings'] as const).filter((k) => br[k] > limits.blastRadius[k]);
  const detail = { declared: br, limits: limits.blastRadius, exposure };
  if (over.length > 0) return { gate: 'BLAST_RADIUS_WITHIN_POLICY', verdict: 'FAIL', evidence: { ...detail, observed: over.map((k) => `${k} ${br[k]} > ${limits.blastRadius[k]}`).join('; '), required, because: 'the declared reach exceeds the policy' } };
  return { gate: 'BLAST_RADIUS_WITHIN_POLICY', verdict: 'PASS', evidence: { ...detail, observed: `declared ${br.tenants}/${br.sales}/${br.rings} within ${limits.blastRadius.tenants}/${limits.blastRadius.sales}/${limits.blastRadius.rings}`, required, because: 'within the policy limits' } };
}

// ---------------------------------------------------------------------------
// HUMAN_LINE_CLEAR: section 3, from the record.
// ---------------------------------------------------------------------------

export function judgeHumanLineClear(classification: Classification, approval: { readonly method: string; readonly by: string } | null): GateJudgement {
  const required = 'no human line is crossed, or a HUMAN approval by a named person is on the record';
  if (classification.lines.length === 0) return { gate: 'HUMAN_LINE_CLEAR', verdict: 'PASS', evidence: { observed: 'no human line crossed', required, because: 'the declared surface crosses no line', lines: [] } };
  if (approval?.method === 'HUMAN' && approval.by) return { gate: 'HUMAN_LINE_CLEAR', verdict: 'PASS', evidence: { observed: `lines ${classification.lines.join(', ')}; HUMAN approval by ${approval.by} on the record`, required, because: 'a human decided, by name', lines: classification.lines, approvedBy: approval.by } };
  return { gate: 'HUMAN_LINE_CLEAR', verdict: 'FAIL', evidence: { observed: `lines ${classification.lines.join(', ')} crossed; no human approval on the record`, required, because: 'a crossed line is a human decision; the platform cannot clear it', lines: classification.lines } };
}

// ---------------------------------------------------------------------------
// The case: what the record says right now, from the latest row per gate.
// ---------------------------------------------------------------------------

export interface GateRowSummary { readonly gate: Gate; readonly verdict: GateVerdict; readonly evaluatedAt: string; readonly evaluatedBy: string }

export interface ChangeCase {
  /** The policy whose gates were required, or null when none is in force (then the seven platform-evaluable gates plus the two human-recorded ones). */
  readonly policy: { readonly key: string; readonly version: number } | null;
  readonly requiredGates: readonly Gate[];
  readonly latest: readonly GateRowSummary[];
  readonly missing: readonly Gate[];
  readonly failed: readonly Gate[];
  readonly notApplicable: readonly Gate[];
  readonly gatesSatisfied: boolean;
  readonly risk: { readonly score: number | null; readonly ceiling: number | null; readonly within: boolean | null };
  readonly humanLines: readonly string[];
  /** READY: every required gate PASS and risk within the ceiling. HUMAN_DECISION: gates satisfied but a human line stands. NOT_READY otherwise. */
  readonly verdict: 'READY' | 'HUMAN_DECISION' | 'NOT_READY';
  readonly statement: string;
}

export function caseFor(input: {
  readonly policy: { readonly key: string; readonly version: number; readonly definition: PolicyDefinition } | null;
  readonly rows: readonly GateRowSummary[];
  readonly riskScore: number | null;
  readonly humanLines: readonly string[];
}): ChangeCase {
  const requiredGates: readonly Gate[] = input.policy ? input.policy.definition.requiredGates : [...PLATFORM_EVALUABLE_GATES, ...HUMAN_RECORDED_GATES];
  // Latest row per gate: rows arrive newest first.
  const latest = new Map<Gate, GateRowSummary>();
  for (const r of input.rows) if (!latest.has(r.gate)) latest.set(r.gate, r);
  const missing = requiredGates.filter((g) => { const v = latest.get(g)?.verdict; return v === undefined || v === 'SKIPPED'; });
  const failed = requiredGates.filter((g) => latest.get(g)?.verdict === 'FAIL');
  const notApplicable = requiredGates.filter((g) => latest.get(g)?.verdict === 'NOT_APPLICABLE');
  const gatesSatisfied = missing.length === 0 && failed.length === 0 && notApplicable.length === 0;
  const ceiling = input.policy ? input.policy.definition.riskCeiling : null;
  const within = ceiling === null || input.riskScore === null ? null : input.riskScore <= ceiling;
  const verdict: ChangeCase['verdict'] = !gatesSatisfied || within === false ? 'NOT_READY' : input.humanLines.length > 0 ? 'HUMAN_DECISION' : 'READY';
  const parts: string[] = [];
  if (failed.length) parts.push(`failed: ${failed.join(', ')}`);
  if (missing.length) parts.push(`not evaluated: ${missing.join(', ')}`);
  if (notApplicable.length) parts.push(`not applicable (which is not a pass): ${notApplicable.join(', ')}`);
  if (within === false) parts.push(`risk ${input.riskScore} above the policy ceiling ${ceiling}`);
  const statement = verdict === 'READY'
    ? `Every required gate passed${ceiling !== null ? ` and risk ${input.riskScore} is within the ceiling ${ceiling}` : ''}. No human line stands. The platform's case is that this change is safe to approve.`
    : verdict === 'HUMAN_DECISION'
      ? `Every required gate passed${ceiling !== null ? ` and risk ${input.riskScore} is within the ceiling ${ceiling}` : ''}. Line(s) ${input.humanLines.join(', ')} stand: a human decides, by name.`
      : `Not ready: ${parts.join('; ')}.`;
  return { policy: input.policy ? { key: input.policy.key, version: input.policy.version } : null, requiredGates, latest: [...latest.values()], missing, failed, notApplicable, gatesSatisfied, risk: { score: input.riskScore, ceiling, within }, humanLines: input.humanLines, verdict, statement };
}
